import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

/**
 * The light chrome around every real-UI mock on the site. Marketing pages show
 * product surfaces inside this frame rather than illustrations of them; the
 * only place mono type and dark panels are allowed is inside it.
 */
export function ProductFrame({
  title,
  chips,
  live,
  children,
  className,
}: {
  title: string;
  chips?: string[];
  /** Show the pulsing signal dot before the title. */
  live?: boolean;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("product-frame", className)}>
      <div className="flex items-center justify-between gap-4 border-b border-rule bg-mono-100/40 px-4 py-2.5">
        <div className="flex items-center gap-2.5 text-[13px] font-semibold text-ink">
          {live && <span className="signal-dot" aria-hidden />}
          {title}
        </div>
        {chips && chips.length > 0 && (
          <div className="hidden sm:flex gap-1.5">
            {chips.map((c) => (
              <Chip key={c}>{c}</Chip>
            ))}
          </div>
        )}
      </div>
      {children}
    </div>
  );
}

export function Chip({
  children,
  tone = "muted",
  className,
}: {
  children: ReactNode;
  tone?: "muted" | "purple" | "live";
  className?: string;
}) {
  const tones = {
    muted: "border border-rule bg-card text-ink-muted",
    purple: "bg-primary-200 text-primary-900",
    live: "bg-signal/15 text-signal-ink",
  };
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-md px-2 py-0.5 font-mono text-[11px] leading-5",
        tones[tone],
        className
      )}
    >
      {children}
    </span>
  );
}
